/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React from "react";
import {
  APPLICATION_STATUS_DIVORCE,
  APPLICATION_STATUS_DOCUMENTS,
  APPLICATION_STATUS_DONE,
  APPLICATION_STATUS_FINANCIAL,
  APPLICATION_STATUS_PARTNER,
  APPLICATION_STATUS_PENSIONS,
  APPLICATION_STATUS_PERSONAL,
  APPLICATION_STATUS_LEGAL_INSURANCE,
  APPLICATION_STATUS_POWER_OF_ATTORNEY,
  APPLICATION_STATUS_PROCESS_COST_SUPPORT,
  APPLICATION_STATUS_SUMMARY,
} from "./applicationStatus";
import ProgressBar from "@/src/components/ProgressBar/ProgressBar";
import Divorce from "@/src/components/Form/Divorce/Divorce";
import Finance from "@/src/components/Form/Divorce/Finance";
import Pensions from "@/src/components/Form/Divorce/Pensions";
import ProcessCostSupport from "@/src/components/Form/Divorce/ProcessCostSupport";
import Personal from "@/src/components/Form/Divorce/Personal";
import SummaryHintBox from "@/src/components/Form/Common/SummaryHintBox";
import Summary from "@/src/components/Form/Divorce/Summary";
import PowerOfAttorney from "@/src/components/Form/Divorce/PowerOfAttorney";
import UploadIntroText from "@/src/components/Form/Divorce/UploadIntroText";
import Documents from "@/src/components/Form/Divorce/Documents";
import { Navigate } from "react-router";
import { getCaseLink } from "@/src/services/productService";
import { FormSteps } from "@/src/types/FormServiceBase";
import PowerOfAttorneyHintBox from "@/src/components/Form/Divorce/PowerOfAttorney/PowerOfAttorneyHintBox";
import Insurance from "@/src/components/Form/Common/Insurance/Insurance.tsx";
import getProgressBarSteps from "./progressBarSteps";

const formSteps: FormSteps = {
  [APPLICATION_STATUS_DIVORCE]: {
    title: "Antrag - Ehe und Scheidung | Legalbird",
    stepUrl: "ehe-und-scheidung",
    applicationStatus: APPLICATION_STATUS_DIVORCE,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"divorce"} progress={16} />
        <Divorce {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_FINANCIAL]: {
    title: "Antrag - Finanzen | Legalbird",
    stepUrl: "finanzen",
    applicationStatus: APPLICATION_STATUS_FINANCIAL,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"finance"} progress={33} />
        <Finance {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_PENSIONS]: {
    title: "Antrag - Versorgungsausgleich | Legalbird",
    stepUrl: "versorgungsausgleich",
    applicationStatus: APPLICATION_STATUS_PENSIONS,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"finance"} progress={50} />
        <Pensions {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_PROCESS_COST_SUPPORT]: {
    title: "Antrag - Verfahrenskostenhilfe | Legalbird",
    stepUrl: "verfahrenskostenhilfe",
    applicationStatus: APPLICATION_STATUS_PROCESS_COST_SUPPORT,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"finance"} progress={66} />
        <ProcessCostSupport {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_PARTNER]: {
    title: "Antrag - Angaben zum Ehepartner | Legalbird",
    stepUrl: "angaben-partner",
    applicationStatus: APPLICATION_STATUS_PARTNER,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"personal"} progress={75} />
        <Personal personType={"partner"} {...props} key={"partner"} />
      </>
    ),
  },
  [APPLICATION_STATUS_PERSONAL]: {
    title: "Antrag - Ihre Angaben | Legalbird",
    stepUrl: "ihre-angaben",
    applicationStatus: APPLICATION_STATUS_PERSONAL,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active={"personal"} progress={80} />
        <Personal personType={"client"} {...props} key={"client"} />
      </>
    ),
  },
  [APPLICATION_STATUS_LEGAL_INSURANCE]: {
    title: "Antrag - Versicherung | Legalbird",
    stepUrl: "versicherung",
    applicationStatus: APPLICATION_STATUS_LEGAL_INSURANCE,
    render: (props) => (
      <>
        <ProgressBar steps={getProgressBarSteps(props.product.paymentType === "insurance")} active="insurance" progress={90} />
        <Insurance {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_SUMMARY]: {
    title: "Antrag - Überprüfen | Legalbird",
    stepUrl: "ueberpruefen",
    applicationStatus: APPLICATION_STATUS_SUMMARY,
    render: (props) => (
      <>
        <SummaryHintBox />
        <Summary {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_POWER_OF_ATTORNEY]: {
    title: "Antrag - Vollmacht | Legalbird",
    stepUrl: "vollmacht",
    applicationStatus: APPLICATION_STATUS_POWER_OF_ATTORNEY,
    render: (props) => (
      <>
        <PowerOfAttorneyHintBox product={props.product} />
        <PowerOfAttorney {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_DOCUMENTS]: {
    title: "Antrag - Dokumente hochladen | Legalbird",
    stepUrl: "dokumente-hochladen",
    applicationStatus: APPLICATION_STATUS_DOCUMENTS,
    render: (props) => (
      <>
        <UploadIntroText divorce={props.product} />
        <Documents {...props} />
      </>
    ),
  },
  [APPLICATION_STATUS_DONE]: {
    title: "Meine Scheidung | Legalbird",
    stepUrl: "",
    applicationStatus: APPLICATION_STATUS_DONE,
    render: (props) => <Navigate to={getCaseLink(props.product)} />,
  },
};

export default formSteps;
